import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add as AddIcon, PersonAdd as PersonAddIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, Edit as EditIcon } from '@mui/icons-material';
import { teamService, userService, installationService } from '../services/api';

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [viewMembersDialogOpen, setViewMembersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [selectedLeader, setSelectedLeader] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [teamsResponse, workersResponse, installationsResponse] = await Promise.all([
        teamService.getTeams(),
        userService.getUsers({ role: 'worker' }),
        installationService.getInstallations(),
      ]);
      setTeams(teamsResponse.data);
      setWorkers(workersResponse.data);
      setInstallations(installationsResponse.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      await teamService.createTeam({
        name: teamName,
        description: teamDescription,
      });
      setSuccess(`Team "${teamName}" created successfully`);
      setDialogOpen(false);
      setTeamName('');
      setTeamDescription('');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setTeamName(team.name || '');
    setTeamDescription(team.description || '');
    setSelectedLeader(team.leader?._id || team.leader || '');
    setEditDialogOpen(true);
    setError('');
  };

  const handleUpdateTeam = async () => {
    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      await teamService.updateTeam(selectedTeam._id, {
        name: teamName,
        description: teamDescription,
        leader: selectedLeader || null,
      });
      setSuccess(`Team "${teamName}" updated successfully`);
      setEditDialogOpen(false);
      setTeamName('');
      setTeamDescription('');
      setSelectedLeader('');
      setSelectedTeam(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddMember = (team) => {
    setSelectedTeam(team);
    setSelectedWorker('');
    setMemberDialogOpen(true);
    setError('');
  };

  const handleConfirmAddMember = async () => {
    if (!selectedWorker) {
      setError('Please select a worker');
      return;
    }

    try {
      await teamService.addMember(selectedTeam._id, selectedWorker);
      setSuccess(`Worker added to ${selectedTeam.name} successfully`);
      setMemberDialogOpen(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleViewMembers = (team) => {
    setSelectedTeam(team);
    setViewMembersDialogOpen(true);
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await teamService.removeMember(selectedTeam._id, memberId);
      setSuccess('Member removed successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTeam = (team) => {
    setSelectedTeam(team);
    setDeleteDialogOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    try {
      await teamService.deleteTeam(selectedTeam._id);
      setSuccess(`Team "${selectedTeam.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setSelectedTeam(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // Get available workers (not in selected team)
  const getAvailableWorkers = () => {
    if (!selectedTeam) return workers;
    const teamMemberIds = selectedTeam.members.map(m => m._id || m);
    return workers.filter(w => !teamMemberIds.includes(w._id));
  };

  /**
   * Get total installations for a team
   */
  const getTeamInstallationCount = (teamId) => {
    return installations.filter((inst) => inst.team?._id === teamId).length;
  };

  /**
   * Get today's installations for a team
   */
  const getTodayInstallationCount = (teamId) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return installations.filter((inst) => {
      if (inst.team?._id !== teamId) return false;
      
      const instDate = new Date(inst.installation_date);
      return instDate >= today && instDate < tomorrow;
    }).length;
  };

  /**
   * Get total meter credit for team members
   */
  const getTeamTotalCredit = (team) => {
    const memberIds = team.members.map(m => m._id || m);
    const teamWorkers = workers.filter(w => memberIds.includes(w._id));
    return teamWorkers.reduce((total, worker) => total + (worker.meter_credit || 0), 0);
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Team Name',
      width: 160,
      renderCell: (params) => (
        <strong>{params.value}</strong>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 200,
    },
    {
      field: 'members',
      headerName: 'Members',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={`${params.value.length}`}
          color='primary'
          size='small'
        />
      ),
    },
    {
      field: 'installations',
      headerName: 'Installations',
      width: 120,
      renderCell: (params) => {
        const count = getTeamInstallationCount(params.row._id);
        return (
          <Chip
            label={`${count} done`}
            color='success'
            variant='outlined'
            size='small'
          />
        );
      },
    },
    {
      field: 'today_installations',
      headerName: 'Total Installations This Day',
      width: 200,
      renderCell: (params) => {
        const count = getTodayInstallationCount(params.row._id);
        return (
          <Chip
            label={`${count}`}
            color={count > 0 ? 'success' : 'default'}
            variant={count > 0 ? 'filled' : 'outlined'}
            size='small'
          />
        );
      },
    },
    {
      field: 'total_credit',
      headerName: 'Total Credit',
      width: 110,
      renderCell: (params) => {
        const totalCredit = getTeamTotalCredit(params.row);
        return (
          <Chip
            label={`${totalCredit}`}
            color={totalCredit > 0 ? 'success' : 'error'}
            size='small'
          />
        );
      },
    },
    {
      field: 'leader',
      headerName: 'Team Leader',
      width: 120,
      valueGetter: (params) => params.row.leader?.username || 'Not assigned',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size='small'
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 100,
      valueGetter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 360,
      renderCell: (params) => (
        <Box>
          <Button
            variant='outlined'
            size='small'
            startIcon={<VisibilityIcon />}
            onClick={() => handleViewMembers(params.row)}
            sx={{ mr: 1 }}
          >
            View
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='primary'
            startIcon={<EditIcon />}
            onClick={() => handleEditTeam(params.row)}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant='contained'
            size='small'
            startIcon={<PersonAddIcon />}
            onClick={() => handleAddMember(params.row)}
            sx={{ mr: 1 }}
          >
            Add
          </Button>
          <Button
            variant='outlined'
            size='small'
            color='error'
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteTeam(params.row)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='h4'>Teams Management</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => {
            setDialogOpen(true);
            setError('');
          }}
        >
          Create Team
        </Button>
      </Box>

      {error && !dialogOpen && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={teams}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            autoHeight
            disableRowSelectionOnClick
          />
        </CardContent>
      </Card>

      {/* Create Team Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Create New Team</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin='dense'
            label='Team Name'
            type='text'
            fullWidth
            variant='outlined'
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin='dense'
            label='Description'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={3}
            value={teamDescription}
            onChange={(e) => setTeamDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTeam} variant='contained'>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Edit Team</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin='dense'
            label='Team Name'
            type='text'
            fullWidth
            variant='outlined'
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin='dense'
            label='Description'
            type='text'
            fullWidth
            variant='outlined'
            multiline
            rows={3}
            value={teamDescription}
            onChange={(e) => setTeamDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Team Leader (Optional)</InputLabel>
            <Select
              value={selectedLeader}
              label='Team Leader (Optional)'
              onChange={(e) => setSelectedLeader(e.target.value)}
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {workers.map((worker) => (
                <MenuItem key={worker._id} value={worker._id}>
                  {worker.full_name || worker.username} ({worker.username})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setTeamName('');
            setTeamDescription('');
            setSelectedLeader('');
            setSelectedTeam(null);
            setError('');
          }}>Cancel</Button>
          <Button onClick={handleUpdateTeam} variant='contained'>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={memberDialogOpen} onClose={() => setMemberDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Add Member to {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant='body2' color='text.secondary' paragraph sx={{ mt: 1 }}>
            Current members: <strong>{selectedTeam?.members?.length || 0}</strong>
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Worker</InputLabel>
            <Select
              value={selectedWorker}
              label='Select Worker'
              onChange={(e) => setSelectedWorker(e.target.value)}
            >
              {getAvailableWorkers().map((worker) => (
                <MenuItem key={worker._id} value={worker._id}>
                  {worker.full_name || worker.username} ({worker.username})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getAvailableWorkers().length === 0 && (
            <Alert severity='info' sx={{ mt: 2 }}>
              All workers are already in this team
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemberDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmAddMember} variant='contained' disabled={!selectedWorker}>
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Members Dialog */}
      <Dialog open={viewMembersDialogOpen} onClose={() => setViewMembersDialogOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Team Members - {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          {selectedTeam?.members && selectedTeam.members.length > 0 ? (
            <List>
              {selectedTeam.members.map((member) => (
                <ListItem key={member._id || member} divider>
                  <ListItemText
                    primary={member.full_name || member.username || 'Unknown'}
                    secondary={`Username: ${member.username || 'N/A'} | Credit: ${member.meter_credit || 0}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge='end'
                      aria-label='delete'
                      onClick={() => handleRemoveMember(member._id || member)}
                      color='error'
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Alert severity='info'>This team has no members yet</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMembersDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setViewMembersDialogOpen(false);
              handleAddMember(selectedTeam);
            }}
            variant='contained'
            startIcon={<PersonAddIcon />}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Team Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete the team <strong>"{selectedTeam?.name}"</strong>?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            This will soft delete the team. The team will be marked as inactive and will not appear in the list.
            {selectedTeam?.members && selectedTeam.members.length > 0 && (
              <Alert severity='warning' sx={{ mt: 2 }}>
                This team has {selectedTeam.members.length} member(s). They will not be removed from the team, but the team will be inactive.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

